import {differenceInDays, differenceInMinutes, isAfter} from 'date-fns';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import pipe from 'lodash/fp/pipe';
import {AttendeeAvailability, AttendeeAvailabilityStatus, MeetingAttendee, MeetingAttendeeType} from '../entities';
import {bySortingArray, getStartOfDay} from '@lib/utils';

export const isMeetingDateTimeRangeTooBig = (start: Date, end: Date) =>
  pipe(differenceInDays, Math.abs)(start, end) >= 14;

export const isMeetingDateTimeRangeTooSmall = (start: Date, end: Date) =>
  pipe(differenceInMinutes, Math.abs)(start, end) < 5;

export const isMeetingDateTimeRangeValid = (start: Date, end: Date) =>
  isAfter(end, start) && !isMeetingDateTimeRangeTooBig(start, end) && !isMeetingDateTimeRangeTooSmall(start, end);

export const countAttendeeAvailabilitiesOnSite = (attendeeAvailabilities?: AttendeeAvailability[]) =>
  attendeeAvailabilities?.filter(({shiftBuildingId}) => shiftBuildingId).length ?? 0;

export const countAvailableAttendees = (attendeeAvailabilities?: AttendeeAvailability[]) =>
  attendeeAvailabilities?.filter(({availability}) => availability === AttendeeAvailabilityStatus.Available).length ?? 0;

export const isFulfilled = <T>(input: PromiseSettledResult<T>): input is PromiseFulfilledResult<T> =>
  input.status === 'fulfilled';

export const groupAndSortAttendeesByLocationType = (meetingAttendees: MeetingAttendee[], date: string) => {
  return map(
    groupBy(meetingAttendees, (item) => getLocationForDate(item, date).locationType),
    (items, key) => ({
      locationType: key as MeetingAttendeeType,
      attendeesNumber: items.length,
    }),
  ).sort(
    bySortingArray('locationType', [
      MeetingAttendeeType.Local,
      MeetingAttendeeType.Remote,
      MeetingAttendeeType.Unknown,
    ]),
  );
};

export const getLocationForDate = (attendee: MeetingAttendee, date: string | Date) => {
  const startOfDate = (typeof date === 'string' ? getStartOfDay(date) : date).toISOString();

  return (
    attendee.locationInfoPerDate[startOfDate] ?? {
      locationType: MeetingAttendeeType.Unknown,
      shiftBuildingName: undefined,
      shiftBuildingId: undefined,
    }
  );
};
